import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { AdminEventProvider } from '@/contexts/AdminEventContext';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';
import { Button } from '@/components/ui/button';
import { LogIn } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * Persistent chrome for the whole admin area: renders the navigation sidebar
 * around every /admin/* page so the menu stays visible and highlights the
 * current location everywhere. On non-admin routes it renders children as-is.
 */
export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const isAdminRoute =
    location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  if (!isAdminRoute) return <>{children}</>;

  return <AdminChrome pathname={location.pathname}>{children}</AdminChrome>;
}

/**
 * Only mounted on admin routes, so the badge queries and the notifications
 * realtime subscription don't run on public pages.
 */
function AdminChrome({ children, pathname }: { children: React.ReactNode; pathname: string }) {
  const navigate = useNavigate();
  const { unreadCount } = useAdminNotifications();
  const [stats, setStats] = useState({ pendingApplications: 0, pendingEventDrafts: 0, pendingOrders: 0, openLeads: 0, activeAffiliates: 0 });

  // Validate the session (server-side, catches expired tokens). If it's gone,
  // show a clear "session expired" prompt instead of silently-empty pages.
  const [authState, setAuthState] = useState<'checking' | 'authed' | 'unauthed'>('checking');
  useEffect(() => {
    let cancelled = false;
    // Use getSession (not getUser): it returns the stored session and silently
    // refreshes an expired-but-refreshable token, so we don't falsely show
    // "session expired" when the access token just needs a refresh.
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setAuthState(data.session ? 'authed' : 'unauthed');
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthState(session ? 'authed' : 'unauthed');
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ count: pendingSpeakers }, { count: pendingVolunteers }] = await Promise.all([
          supabase.from('speaker_applications').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('volunteer_applications').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        ]);
        const pendingApplications = (pendingSpeakers || 0) + (pendingVolunteers || 0);

        const eventTables = ['festivals', 'academies', 'retreats', 'workations'] as const;
        const counts = await Promise.all(
          eventTables.map(t =>
            supabase.from(t).select('*', { count: 'exact', head: true }).eq('status', 'pending_review')
          )
        );
        const pendingEventDrafts = counts.reduce((sum, r) => sum + (r.count || 0), 0);

        // Orders needing attention: open invoices + any order from the last 24h
        const since24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const { count: pendingOrders } = await supabase
          .from('nomadfest_orders')
          .select('*', { count: 'exact', head: true })
          .neq('status', 'cancelled')
          .or(`status.eq.pending_invoice,created_at.gte.${since24h}`);

        const { count: openLeads } = await supabase
          .from('contact_messages')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'new');

        // Only real affiliates — exclude the auto-created per-user accounts
        // (commission_rate 0, no referrals) that otherwise inflate the count.
        const { count: activeAffiliates } = await supabase
          .from('affiliate_partners')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true)
          .or('commission_rate.gt.0,total_referrals.gt.0');

        if (!cancelled) {
          setStats({
            pendingApplications,
            pendingEventDrafts,
            pendingOrders: pendingOrders || 0,
            openLeads: openLeads || 0,
            activeAffiliates: activeAffiliates || 0,
          });
        }
      } catch {
        /* badges are best-effort; ignore fetch errors */
      }
    })();
    return () => { cancelled = true; };
    // Refresh counts when moving between admin pages.
  }, [pathname]);

  if (authState === 'unauthed') {
    return (
      <div className="container mx-auto pt-28 pb-16 px-4">
        <div className="max-w-md mx-auto text-center border rounded-xl p-8 bg-card">
          <LogIn className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-bold mb-2">Session expired</h2>
          <p className="text-sm text-muted-foreground mb-6">
            You've been signed out. Please sign in again to access the admin area.
          </p>
          <Button onClick={() => navigate('/auth')}>Sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <AdminEventProvider>
      <SidebarProvider>
        <AdminSidebar stats={{ ...stats, unreadMessages: unreadCount }} />
        <SidebarInset>
          {/* Mobile-only trigger to open the off-canvas sidebar. */}
          <SidebarTrigger className="md:hidden fixed left-3 top-[100px] z-40 bg-background border shadow-sm" />
          {children}
        </SidebarInset>
      </SidebarProvider>
    </AdminEventProvider>
  );
}
